import { LessonStatus } from '@/lib/constants';
import { CATALOG_STALE_TIME, queryKeys, USER_DATA_STALE_TIME } from '@/lib/queryClient';
import {
  buildUserLessonsMap,
  fetchLessonsForCourse,
  fetchUserLessonsForCourse,
} from '@/supabase/services/optimizedCourse';
import type { Lesson, UserLesson } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Hook for simple course (type === 0).
 * Fetches only flat lessons (by order_no) + user progress.
 * No sections, no Continue watch, no Latest, no collections.
 */
export function useSimpleCourseData(
  courseId: number | undefined,
  userId: string | null | undefined,
  minStatus: number = LessonStatus.Published
) {
  const lessonsQuery = useQuery({
    queryKey: queryKeys.course.lessons(courseId!, minStatus),
    queryFn: () => fetchLessonsForCourse(courseId!, minStatus),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const userLessonsQuery = useQuery({
    queryKey: queryKeys.userLessons.byCourse(userId || null, courseId!),
    queryFn: () => fetchUserLessonsForCourse(userId || null, courseId!, false),
    staleTime: USER_DATA_STALE_TIME,
    enabled: !!courseId,
    gcTime: 1 * 60 * 1000,
  });

  const userLessonsMap: Record<number, UserLesson> = useMemo(
    () =>
      userLessonsQuery.data?.allUserLessons
        ? buildUserLessonsMap(userLessonsQuery.data.allUserLessons)
        : {},
    [userLessonsQuery.data?.allUserLessons]
  );

  return {
    lessons: lessonsQuery.data ?? ([] as Lesson[]),
    userLessonsMap,
    isLoading: lessonsQuery.isLoading,
    error: lessonsQuery.error || userLessonsQuery.error,
    queries: { lessonsQuery, userLessonsQuery },
  };
}
